import {
    ColoursObj,
    DetailedMapControls,
    EventrMap,
    Gutters,
    Icon,
    MainButton,
    ManualInputAddress,
    safeFloatParse,
    updateDisplayName,
} from '@eventr/common-storybook'
import { NavigationView } from '../../layout/NavigationView'
import { gwApiClient } from '../../../data/apiClients'
import { AddressLookupSelectedAddress } from '@eventr/common'
import { Stack, Box } from '@mui/material'
import { generalTranslation } from '../../../i18n/translateHelpers'
import { useGlobalRouter } from '../../globalRouter/useGlobalRouter'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AddressLabels } from '../formComponents/formAddressInput'
import { useItinAddress } from '../hooks/useEditItem'
import { MapRef } from 'react-map-gl/maplibre'

export interface IAddressPageBaseProps {
    onSave: (val: AddressLookupSelectedAddress | undefined) => void
    value?: AddressLookupSelectedAddress | undefined
    centerAround?: {
        latitude: number
        longitude: number
    }
    defaultToItin?: boolean
}
export const BaseAddressPage = ({
    onSave,
    value,
    centerAround,
    defaultToItin,
}: IAddressPageBaseProps) => {
    const { goBack } = useGlobalRouter()
    const ref = useRef<MapRef>(null)

    const [zoom, setZoom] = useState<number>(14)
    const [modifiedAddress, setModifiedAddress] = useState<
        AddressLookupSelectedAddress | undefined
    >(value)
    const itinAddress = useItinAddress()
    useEffect(() => {
        if (value) {
            setModifiedAddress(value)
        }
        if (defaultToItin) {
            if (itinAddress) {
                setModifiedAddress(itinAddress)
            }
        }
    }, [value, defaultToItin, itinAddress])
    const [isLockedPinUpdate, setLockedPinUpdate] = useState(false)
    const saveActions = useMemo(() => {
        return [
            {
                useButton: true,
                disabled:
                    JSON.stringify(value) === JSON.stringify(modifiedAddress),
                buttonText: generalTranslation('done'),
                action: () => {
                    onSave(modifiedAddress)
                    goBack()
                },
                id: 'done',
            },
        ]
    }, [value, modifiedAddress, goBack, onSave])

    const initialViewState = useMemo(() => {
        if (value && value.lat && value.lon) {
            return {
                longitude: safeFloatParse(value.lon),
                latitude: safeFloatParse(value.lat),
                zoom: 14,
            }
        }
        if (defaultToItin) {
            if (itinAddress) {
                return {
                    longitude: safeFloatParse(itinAddress.lon),
                    latitude: safeFloatParse(itinAddress.lat),
                    zoom: 14,
                }
            }
        }
        if (centerAround) {
            return {
                longitude: centerAround.longitude,
                latitude: centerAround.latitude,
                zoom: 14,
            }
        }
        return undefined
    }, [value, centerAround, defaultToItin, itinAddress])

    useLayoutEffect(() => {
        setTimeout(() => {
            if (ref.current && initialViewState) {
                ref.current?.flyTo({
                    center: [
                        initialViewState.longitude,
                        initialViewState.latitude,
                    ],
                    zoom: initialViewState.zoom,
                    duration: 100,
                })
            }
        }, 50)
    }, [initialViewState])

    const pinLockIcons = useMemo(() => {
        if (isLockedPinUpdate) {
            return (
                <Icon
                    onClick={() => {
                        setLockedPinUpdate(false)
                    }}
                    iconColour={ColoursObj.core.secondary}
                    name="Locked"
                />
            )
        }
        return (
            <Icon
                onClick={() => {
                    setLockedPinUpdate(true)
                }}
                name="Unlocked"
            />
        )
    }, [isLockedPinUpdate])
    return (
        <NavigationView
            navigationBarTitle={generalTranslation('choose address')}
            backButtonAction={() => {
                goBack()
            }}
            navigationBarTrailing={saveActions}
            usePageTransition
        >
            <Gutters topPadding="0px">
                <Stack>
                    <EventrMap
                        mapRef={ref}
                        initialViewState={initialViewState}
                        reuseMaps={true}
                        zoom={zoom}
                        onZoom={(e) => {
                            setZoom(e.viewState.zoom)
                        }}
                        height="250px"
                        containerStyle={{ borderRadius: '8px' }}
                    >
                        <DetailedMapControls
                            setModifiedAddress={setModifiedAddress}
                            apiClient={gwApiClient}
                            selectedAddress={modifiedAddress}
                            isLockedPinUpdate={isLockedPinUpdate}
                        />
                    </EventrMap>
                    <ManualInputAddress
                        apiClient={gwApiClient}
                        onAddressSelected={(address) => {
                            setModifiedAddress(address)
                        }}
                        // centerAroundFallBack={centerAround}
                        onChangeSetAddress={function (
                            value: string,
                            key?: string,
                            keys?: string[]
                        ): void {
                            if (!modifiedAddress) return
                            if (key) {
                                let newAddress:
                                    | AddressLookupSelectedAddress
                                    | undefined = {
                                    ...modifiedAddress,
                                    address: {
                                        ...modifiedAddress.address,
                                        [key]: value,
                                    },
                                    type: '',
                                }

                                if (key === 'name') {
                                    // remove some properties that are not needed in the display name
                                    delete newAddress?.address?.house_number
                                }
                                newAddress = updateDisplayName(newAddress)
                                setModifiedAddress(newAddress)
                            } else if (keys && keys.length > 0) {
                                var tmpAddress: any = {
                                    ...modifiedAddress.address,
                                }
                                keys.forEach((addressKey: string) => {
                                    tmpAddress[addressKey] = value
                                })
                                setModifiedAddress({
                                    ...modifiedAddress,
                                    address: tmpAddress,
                                })
                            }
                        }}
                        disabled={false}
                        readOnly={false}
                        addressLabels={AddressLabels}
                        selectedAddress={modifiedAddress}
                        textFieldProps={{
                            InputProps: {
                                endAdornment: pinLockIcons,
                            },
                        }}
                    ></ManualInputAddress>
                    <Box height={'25px'}></Box>
                    <MainButton
                        onClick={() => {
                            onSave({ timeZone: value?.timeZone })
                            goBack()
                        }}
                        size="small"
                        variant="destructiveBorderless"
                    >
                        {generalTranslation('clear')}
                    </MainButton>
                    <Box height={'25px'}></Box>
                </Stack>
            </Gutters>
        </NavigationView>
    )
}
